import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HomeIcon from '@mui/icons-material/Home';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, rgba(198, 40, 40, 0.05) 0%, rgba(142, 0, 0, 0.02) 100%)',
  padding: '80px 0 40px 0',
  // Voir DeclarationConfidentialite : centrer à ce niveau centrait tout le
  // document. Seuls l'en-tête et le pied de page sont centrés explicitement.
  position: 'relative',
  overflow: 'hidden',
  width: '100%'
});

const PageContainer = styled(Container)({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 24px',
  '@media (max-width: 600px)': {
    padding: '0 16px'
  }
});

const StyledPaper = styled(Paper)({
  padding: '48px',
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(198, 40, 40, 0.1)',
  marginBottom: '32px',
  '@media (max-width: 600px)': {
    padding: '24px'
  }
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  color: '#c62828',
  marginBottom: '20px',
  paddingBottom: '10px',
  // Un filet sous le titre sépare les sections sans recourir à un encadré :
  // la hiérarchie se lit d'un coup d'œil dans un document long.
  borderBottom: '2px solid rgba(198, 40, 40, 0.15)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontSize: '1.5rem',
  letterSpacing: '-0.01em',
  '@media (max-width: 600px)': {
    fontSize: '1.25rem'
  }
});

const SubSectionTitle = styled(Typography)({
  fontWeight: 600,
  color: '#333',
  marginBottom: '16px',
  marginTop: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '1.25rem'
});

// Fond blanc plutôt que gris : la carte se détache du papier au lieu de s'y
// fondre, et le texte long y est plus confortable à lire.
const RuleCard = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '14px',
  padding: '8px 24px',
  marginBottom: '32px',
  border: '1px solid #ececec',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  display: 'flex',
  flexDirection: 'column'
});

const RuleItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '18px',
  padding: '20px 0',
  // Filet plein et très clair au lieu de pointillés : les pointillés donnaient
  // un aspect de brouillon, et l'espacement resserré tassait les règles.
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none'
  }
});

// L'icône devient une pastille : elle se lit comme un repère visuel plutôt que
// comme un symbole posé au bord du texte.
const RuleIcon = styled(Box)({
  minWidth: '38px',
  width: '38px',
  height: '38px',
  flexShrink: 0,
  borderRadius: '50%',
  backgroundColor: 'rgba(198, 40, 40, 0.08)',
  color: '#c62828',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const RuleContent = styled(Box)({
  flex: 1
});

const ImportantNotice = styled(Box)({
  backgroundColor: '#fff3e0',
  borderLeft: '4px solid #c62828',
  padding: '20px',
  borderRadius: '8px',
  marginBottom: '24px'
});

const Badge = styled(Box)({
  backgroundColor: 'rgba(198, 40, 40, 0.1)',
  padding: '4px 12px',
  borderRadius: '20px',
  color: '#c62828',
  fontSize: '0.875rem',
  fontWeight: 500
});

const FooterNote = styled(Box)({
  marginTop: '48px',
  padding: '24px',
  backgroundColor: '#f8f8f8',
  borderRadius: '16px',
  border: '1px solid #e0e0e0',
  textAlign: 'center'
});

const ConditionsInscription = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <HeroSection>
        <PageContainer>
          <StyledPaper elevation={0}>
            {/* En-tête */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mb: 4 
            }}>
              <AssignmentIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#c62828',
                  opacity: 0.8,
                  mb: 2
                }} 
              />
              <Typography variant="h1" sx={{ 
                fontWeight: 800, 
                mb: 2,
                color: '#333',
                fontSize: { xs: '2rem', md: '3rem' },
                textAlign: 'center'
              }}>
                Conditions générales d'inscription
              </Typography>
              <Typography variant="subtitle1" sx={{ color: '#666', textAlign: 'center', mb: 2 }}>
                Dernière mise à jour : 22/08/2026
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                justifyContent: 'center' 
              }}>
                <Badge>🇨🇦 Formation en secourisme</Badge>
                <Badge>Partenaire de la Croix-Rouge canadienne</Badge>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: 'rgba(198, 40, 40, 0.2)' }} />

            {/* Avis important */}
            <ImportantNotice>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <InfoIcon sx={{ color: '#c62828', fontSize: 28 }} />
                <Typography variant="body1">
                  <strong>Veuillez lire attentivement</strong> les conditions ci-dessous avant de procéder à votre inscription. 
                  En vous inscrivant à nos formations, vous acceptez l'ensemble de ces conditions.
                </Typography>
              </Box>
            </ImportantNotice>

            {/* SECTION 1 - PAIEMENT ET FACTURATION */}
            <SectionTitle variant="h4">
              <PaymentIcon /> 1. Paiement et facturation
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleIcon>
                  <PaymentIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Carte bancaire ou virement Interac
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Le paiement par <strong>carte bancaire</strong> s'effectue en dollars canadiens
                    sur une page sécurisée de notre prestataire Stripe : votre numéro de carte est
                    saisi chez lui et ne transite jamais par nos serveurs. Vous pouvez également
                    régler par <strong>virement Interac</strong> à l'adresse indiquée dans le
                    courriel qui vous est envoyé. La <strong>TPS et la TVQ</strong> sont calculées
                    et détaillées avant toute validation.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>
                  <InfoIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Votre place est réservée dès le paiement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les renseignements saisis sont enregistrés dès l'ajout au panier, afin que rien
                    ne soit perdu si vous êtes interrompu. <strong>Votre inscription n'est toutefois
                    confirmée qu'une fois le paiement reçu.</strong> Par virement Interac, elle
                    reste au statut « en attente » jusqu'à réception du montant : tant qu'elle
                    n'est pas confirmée, la place n'est pas garantie.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>
                  <EmailIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Confirmation et facture par courriel
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Une confirmation d'inscription et une <strong>facture au format PDF</strong> vous
                    sont envoyées automatiquement après le paiement. Si vous ne recevez rien dans
                    l'heure qui suit, vérifiez votre dossier de courriels indésirables avant de
                    nous écrire.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>
                  <WarningIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Si le paiement échoue
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aucun montant n'est prélevé et aucune inscription n'est créée. Vos informations
                    restent enregistrées et vous pouvez reprendre là où vous vous étiez arrêté, ou
                    nous écrire pour que nous finalisions avec vous.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 2 - POLITIQUE DE REMBOURSEMENT */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <PaymentIcon /> 2. Politique de remboursement
            </SectionTitle>

            <RuleCard>
              {/* Règle 1 - 50% non remboursables */}
              <RuleItem>
                <RuleIcon>
                  <WarningIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Cours en ligne et livre électronique
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    En cas d'annulation par le participant, les frais d'inscription pour un cours en ligne 
                    et du livre électronique avec la Croix-Rouge d'un montant de <strong>50% des frais d'inscription</strong> 
                    ne sont pas remboursables.
                  </Typography>
                </RuleContent>
              </RuleItem>

              {/* Règle 2 - Délai 48h */}
              <RuleItem>
                <RuleIcon>
                  <InfoIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Délai de 48 heures
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pour recevoir un remboursement du reste du montant payé, il faut nous aviser <strong>au moins 48 heures avant la date du début du cours</strong>. 
                    Après ce délai, <strong>aucun remboursement n'est possible</strong>.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 3 - CERTIFICATS ET COMMUNICATIONS */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <EmailIcon /> 3. Certificats et communications
            </SectionTitle>

            <RuleCard>
              {/* Règle 1 - Envoi des certificats */}
              <RuleItem>
                <RuleIcon>
                  <EmailIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Envoi des certificats
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Le certificat de réussite est envoyé au participant par la Croix-Rouge canadienne par courriel.
                  </Typography>
                </RuleContent>
              </RuleItem>

              {/* Règle 2 - Un courriel par participant */}
              <RuleItem>
                <RuleIcon>
                  <WarningIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#c62828' }}>
                    IMPORTANT : Un seul utilisateur par adresse courriel
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    N'utilisez pas le même courriel pour 2 participants. Il faut utiliser deux adresses courriels différentes.
                  </Typography>
                </RuleContent>
              </RuleItem>

              {/* Règle 3 - Exactitude des courriels */}
              <RuleItem>
                <RuleIcon>
                  <InfoIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Exactitude des courriels
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Veuillez vous assurer que les adresses courriels sont exactes pour la réception de nos communications 
                    et des certificats par la Croix-Rouge canadienne.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 4 - ENVOI DU MANUEL */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <MenuBookIcon /> 4. Envoi du manuel
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleIcon>
                  <MenuBookIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Responsabilité
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Pour l'envoi du manuel, nous ne sommes pas responsables des erreurs d'adresse fournie.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Des frais seront demandés pour un changement d'adresse après l'envoi.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 5 - FORMATION JEUNESSE */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <ChildCareIcon /> 5. Formation jeunesse
            </SectionTitle>

            <RuleCard>
              {/* Sous-section - Visioconférence */}
              <SubSectionTitle>
                <ZoomInIcon sx={{ fontSize: 20 }} /> Formation jeunesse en visioconférence
              </SubSectionTitle>

              <RuleItem>
                <RuleIcon>
                  <ZoomInIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    Pour la formation jeunesse en visioconférence, veuillez vous assurer que l'adresse courriel 
                    est exacte pour la réception du lien Zoom.
                  </Typography>
                </RuleContent>
              </RuleItem>

              {/* Sous-section - Conditions de participation */}
              <SubSectionTitle sx={{ mt: 2 }}>
                <ChildCareIcon sx={{ fontSize: 20 }} /> Conditions de participation
              </SubSectionTitle>

              <RuleItem>
                <RuleIcon>
                  <ChildCareIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Je comprends que mon enfant devra arriver à l'heure et participer au cours en totalité</strong>{' '}
                    pour recevoir son certificat de la Croix-Rouge.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 6 - INSCRIPTION DE GROUPE */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <AssignmentIcon /> 6. Inscription de groupe
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleIcon>
                  <AssignmentIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Une demande, puis une séance réservée à votre groupe
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Une entreprise, une école ou un organisme peut demander une formation pour
                    plusieurs participants. La demande est d'abord <strong>étudiée et approuvée par
                    notre équipe</strong> : elle ne crée pas de séance automatiquement. Une fois
                    approuvée, une séance est ouverte aux date, heure et lieu convenus.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>
                  <EmailIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Lien d'inscription et code d'accès
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    La personne-ressource du groupe reçoit par courriel un <strong>lien
                    d'inscription et un code d'accès</strong> à transmettre aux participants.
                    Chacun s'inscrit ensuite lui-même, ce qui garantit l'exactitude de son nom et
                    de son courriel pour la certification.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>
                  <InfoIcon />
                </RuleIcon>
                <RuleContent>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Séance réservée ou ouverte
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Par défaut, la séance d'un groupe est <strong>réservée à ses participants</strong> et
                    n'apparaît pas au calendrier public. Elle peut, à la demande du groupe, être
                    ouverte à d'autres apprenants afin de compléter les places disponibles.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 7 - CONDITIONS GÉNÉRALES */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <AssignmentIcon /> 7. Conditions générales
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    L'inscription est personnelle et non transférable.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    Le participant s'engage à fournir des informations exactes et complètes.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    La présence à l'heure et la participation à l'ensemble de la formation sont obligatoires pour l'obtention du certificat.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    En cas d'annulation d'une formation par VitaSecours, un remboursement intégral ou un report sera proposé.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 8 - RESPONSABILITÉS */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <InfoIcon /> 8. Responsabilités
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    Le participant est responsable de la vérification de ses informations personnelles.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    VitaSecours n'est pas responsable des problèmes techniques liés à la connexion internet du participant.
                  </Typography>
                </RuleContent>
              </RuleItem>

              <RuleItem>
                <RuleIcon>•</RuleIcon>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary">
                    La Croix-Rouge canadienne est seule responsable de l'émission des certificats.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* SECTION 9 - PROTECTION DES DONNÉES */}
            <SectionTitle variant="h4" sx={{ mt: 4 }}>
              <InfoIcon /> 9. Protection des renseignements personnels
            </SectionTitle>

            <RuleCard>
              <RuleItem>
                <RuleContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les informations collectées lors de l'inscription sont utilisées uniquement dans le cadre 
                    de la gestion de la formation et de l'émission des certificats par la Croix-Rouge canadienne.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pour plus de détails, consultez notre{' '}
                    <Link component={RouterLink} to="/declaration-confidentialite" sx={{ color: '#c62828' }}>
                      Politique de confidentialité
                    </Link>.
                  </Typography>
                </RuleContent>
              </RuleItem>
            </RuleCard>

            {/* Résumé des points clés */}
            <Box sx={{ 
              mt: 6, 
              p: 3, 
              backgroundColor: '#e8f5e8', 
              borderRadius: '12px',
              border: '1px solid #4caf50'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2e7d32' }}>
                ✓ RÉSUMÉ DES POINTS IMPORTANTS
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1
              }}>
                <Typography variant="body2">• 50% des frais sont non remboursables pour les cours en ligne avec livre électronique</Typography>
                <Typography variant="body2">• Annulation possible jusqu'à 48h avant le début du cours</Typography>
                <Typography variant="body2">• Un courriel unique par participant pour les certificats</Typography>
                <Typography variant="body2">• Vérifiez l'exactitude de vos informations (courriel, adresse)</Typography>
                <Typography variant="body2">• Présence obligatoire pour toute la durée de la formation</Typography>
              </Box>
            </Box>

            {/* Note de bas de page */}
            <FooterNote>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>vitasecours.ca</strong> - Formation en secourisme, partenaire de la Croix-Rouge canadienne
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Exploité par <strong>Amira Ben Nasr</strong>
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0'
              }}>
                <Typography variant="caption" color="text.secondary">
                  © {currentYear} vitasecours.ca
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  275 Bd des Laurentides, Saint-Jérôme, QC J7Z 7K8
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Link href="mailto:info@vitasecours.ca" sx={{ color: '#666' }}>info@vitasecours.ca</Link>
                </Typography>
              </Box>
            </FooterNote>

            {/* Bouton retour */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                startIcon={<HomeIcon />}
                sx={{
                  borderColor: '#c62828',
                  color: '#c62828',
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  '&:hover': {
                    borderColor: '#8e0000',
                    backgroundColor: 'rgba(198, 40, 40, 0.05)',
                  }
                }}
              >
                Retour à l'accueil
              </Button>
            </Box>
          </StyledPaper>
        </PageContainer>
      </HeroSection>
    </Box>
  );
};

export default ConditionsInscription;